import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import {
  getUserProfile,
  createUserProfile,
  getApiKey,
  getUsageLogs,
  getUserProjects,
  ApiKey,
  UsageLog,
  UserProfile
} from '../services/apiService';
import type { Session } from '@supabase/supabase-js';

export function useUserDashboard() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [apiKey, setApiKey] = useState<ApiKey | null>(null);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'setup' | 'dashboard'>('setup');
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let initialized = false;
  
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (!initialized && session?.user?.id) {
        initialized = true;
        setSession(session);
      }
    });
  
    supabase.auth.getSession().then(({ data }) => {
      if (!initialized && data.session?.user?.id) {
        initialized = true;
        setSession(data.session);
      }
    });
  
    return () => {
      subscription?.subscription.unsubscribe();
    };
  }, []);
  

  useEffect(() => {
    if (!session?.user?.id) return;
    initializeDashboard(session);
  }, [session]);

  const initializeDashboard = useCallback(async (session: Session) => {
    setLoading(true);
    setError(null);
  
    try {
      const userId = session.user.id;
      const email = session.user.email || 'New User';
      await createUserProfile(userId, email);
      const profileData = await getUserProfile(userId);
  
      setProfile(profileData);
      const projects = await getUserProjects(userId);
  
      if (projects.length === 0) {
        setStep('setup');
        setLoading(false);
        return;
      }
  
      setStep('dashboard');
  
      const keyData = await getApiKey(userId);
      setApiKey(keyData);
  
      if (keyData) {
        const logs = await getUsageLogs(userId);
        setUsageLogs(logs);
      } else {
        setUsageLogs([]);
      }
    } catch (e: any) {
      
      setError(e.message || 'Unexpected error');
    } finally {
      setLoading(false);
    }
  }, []);
  

  const completeSetup = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error('No active session found.');
      }

      const projects = await getUserProjects(session.user.id);
      if (projects.length === 0) {
        throw new Error('No projects found after setup.');
      }

      const keyData = await getApiKey(session.user.id);
      setApiKey(keyData);

      if (keyData) {
        const logs = await getUsageLogs(session.user.id);
        setUsageLogs(logs);
      }

      setStep('dashboard');
    } catch (e: any) {
      
      setError(e.message || 'Unexpected error during setup completion');
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, profile, apiKey, usageLogs, error, step, initializeDashboard, completeSetup };
}
